import React, { useState, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { HelpCircle, BookOpen, MessageSquare, X } from 'lucide-react';

/**
 * Floating, context-aware Help button (FAB). Appears bottom-right of every
 * dashboard page. Opens a dialog with relevant tips for the current route
 * plus links to the full docs and support.
 */
const HELP_TIPS = {
  '/dashboard': [
    'Click "+ New AI Employee" to launch the agent creation wizard.',
    'Use "Connect Channels" to wire WhatsApp, Email, Instagram, FB & web chat to your AI Employees.',
    'Check Usage & Credits in the right column to track your plan consumption.',
  ],
  '/dashboard/chatbots': [
    'Click any AI Employee card to open its full configuration.',
    'You can give each agent its own icon, color, and avatar image.',
    'Names must be unique — duplicates are blocked.',
  ],
  '/dashboard/chatbots/': [
    'Settings → tune persona, tone, and rules. The rules section overrides defaults.',
    'Knowledge → upload PDFs/URLs to ground answers in your business data.',
    'Channels → connect WhatsApp / Email / FB / IG / Website.',
    'Automations → set follow-up sequences and CRM hooks for this agent.',
  ],
  '/dashboard/analytics': [
    'Filter by date range to spot conversion trends.',
    'Each AI Employee has its own funnel — pick one from the dropdown.',
  ],
  '/dashboard/leads': [
    'Drag-and-drop a lead card across pipeline stages to update its status.',
    'Click a lead to see full conversation context.',
  ],
  '/dashboard/automations': [
    'Each automation belongs to one AI Employee.',
    'Use triggers like "lead created" or "tag added" to fire follow-up emails / WhatsApp messages.',
  ],
  '/dashboard/refer-and-earn': [
    'Share your unique link to earn recurring commission on every signup.',
    'Track earnings and pending payouts in the dashboard below.',
  ],
  '/dashboard/settings': [
    'Update your profile, change password, manage email preferences here.',
    'Plan upgrades happen in the Billing tab.',
  ],
  '/dashboard/admin': [
    'Roles tab → create custom roles like Moderator, Sub-Admin with specific segment permissions.',
    'Integrations tab → manage Stripe, LLM providers, and service costs that feed plan pricing.',
    'Bulk-assign roles to multiple users via the Users tab.',
  ],
};

export const GlobalHelpButton = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const tips = useMemo(() => {
    // Find the most specific matching route prefix
    const path = location.pathname;
    const sorted = Object.keys(HELP_TIPS).sort((a, b) => b.length - a.length);
    const match = sorted.find((prefix) => path.startsWith(prefix));
    return HELP_TIPS[match] || [
      'Use the left sidebar to navigate between sections.',
      'Click your profile picture (top-right) for account settings.',
    ];
  }, [location.pathname]);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-gradient-to-br from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 shadow-lg shadow-blue-500/30 p-0"
        data-testid="global-help-fab"
        aria-label="Help"
      >
        <HelpCircle className="w-6 h-6 text-white" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-teal-600" /> Quick Help
            </DialogTitle>
            <DialogDescription>Tips for the {location.pathname.split('/').pop() || 'current'} page.</DialogDescription>
          </DialogHeader>

          <ul className="space-y-2 mt-2">
            {tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-teal-600 font-bold mt-0.5">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>

          <div className="border-t pt-3 mt-3 flex gap-2">
            <Link to="/docs/user-manual" onClick={() => setOpen(false)} className="flex-1">
              <Button variant="outline" className="w-full gap-1" data-testid="help-docs-link">
                <BookOpen className="w-4 h-4" /> Full Docs
              </Button>
            </Link>
            <a href="mailto:support@harmonix.info" className="flex-1">
              <Button variant="outline" className="w-full gap-1" data-testid="help-support-link">
                <MessageSquare className="w-4 h-4" /> Contact Support
              </Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalHelpButton;
